import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/saint-tms-results
 *
 * "Is it really SAINT®?" results page. Pulls the SAINT® trial data
 * (78.6% remission in the 2022 RCT), a patient-stories placeholder
 * (real quotes only with consent), a link to the Coral Springs Google
 * reviews profile, and the standard small print.
 */

const faqs: LearnFAQ[] = [
  {
    question: "What results did SAINT® show in the clinical trials?",
    answer: `In the 2022 double-blind, sham-controlled randomized controlled trial, 78.6% of the active treatment group reached remission after the five-day SAINT® protocol, compared with 13% of the sham group (Cole et al., Am J Psychiatry 2022;179:132–141). An earlier open-label study reported a similar remission rate (Cole et al., Am J Psychiatry 2020;177:716–726). Individual results vary; no outcome is guaranteed. These results apply to the SAINT® system (the FDA-cleared Magnus Neuromodulation System) only and do not transfer to other accelerated TMS protocols.`,
  },
  {
    question: "Do the SAINT® results apply to other accelerated TMS programs?",
    answer: `No. The 78.6% remission figure comes from trials of the SAINT® system specifically — the FDA-cleared Magnus Neuromodulation System with its own fMRI-guided targeting software. Other accelerated TMS protocols may copy the schedule but do not use the same FDA-cleared targeting system, and their own providers state they have not been tested in the SAINT clinical trials.`,
  },
  {
    question: "Where can I read patient reviews of SAINT® at Neuro Wellness?",
    answer: `You can read verified Google reviews for Neuro Wellness TMS Centers of America in Coral Springs, Florida on our Google Business Profile. Individual results vary; no outcome is guaranteed. Patient stories on this page are shared only with signed consent and HIPAA authorization.`,
  },
];

export const pageSaintTmsResults = makeArticle({
  slug: "saint-tms-results",
  title: `SAINT® TMS Results & Reviews: What the Trials and Patients Show`,
  description: `SAINT® TMS results: 78.6% remission in the 2022 randomized trial (Cole et al.), what "remission" means, patient stories, and Google reviews for the Coral Springs clinic.`,
  canonical: "/learn/saint-tms-results",
  h1: "SAINT® Results: Trial Data and Patient Experiences",
  breadcrumbLabel: "SAINT® TMS Results & Reviews",
  shortAnswer: `In the 2022 double-blind, sham-controlled randomized controlled trial, 78.6% of the active treatment group reached remission after the five-day SAINT® protocol (Cole et al., Am J Psychiatry 2022;179:132–141). Individual results vary; no outcome is guaranteed. These results apply to the SAINT® system — the FDA-cleared Magnus Neuromodulation System with its own fMRI-guided targeting software — and do not transfer to other accelerated TMS protocols. If you are in crisis, call or text 988.`,
  keyTakeaways: [
    `78.6% of the active group reached remission in the 2022 SAINT® randomized controlled trial (Cole et al., 2022). Individual results vary.`,
    `The sham (placebo) group had a 13% remission rate in the same trial.`,
    `Remission means a depression score below a set threshold on a standardized scale (MADRS), not just feeling better.`,
    `These results apply to SAINT® only — the FDA-cleared Magnus system — not to other accelerated TMS programs.`,
    `Patient stories and reviews are shared only with signed consent; no story is fabricated.`,
    `If you are in crisis, call or text 988.`,
  ],
  sections: [
    {
      heading: "What the SAINT® trials show",
      paragraphs: [
        `SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) has been studied in a series of trials at Stanford. The most rigorous is the 2022 double-blind, sham-controlled randomized controlled trial: 78.6% of participants who received SAINT® reached remission after the five-day protocol, compared with 13% of those in the sham (placebo) group (Cole et al., Am J Psychiatry 2022;179:132–141). An earlier open-label study from the same group reported a similar remission rate (Cole et al., Am J Psychiatry 2020;177:716–726).`,
        `Remission means a participant's depression score dropped below a set threshold on a standardized mood scale — in the SAINT® trials, the Montgomery-Åsberg Depression Rating Scale (MADRS). A MADRS score of 10 or below generally indicates remission. Remission is stricter than response, which means a meaningful improvement that does not necessarily reach the remission threshold. Individual results vary; no outcome is guaranteed.`,
        `These results apply to the SAINT® system specifically — the FDA-cleared Magnus Neuromodulation System with its proprietary fMRI-guided targeting software (510(k) K220177, September 2022). Other accelerated TMS protocols may copy the five-day, 10-sessions-a-day schedule but do not use the same FDA-cleared targeting system, and their own providers state they have not been tested in the SAINT clinical trials. The SAINT® trial outcomes do not transfer to those protocols. [[See the side-by-side comparison|/learn/saint-vs-accelerated-tms]].`,
      ],
    },
    {
      heading: "Patient stories",
      paragraphs: [
        `We share patient stories, quotes, and video only with signed consent and HIPAA authorization. No patient story on this page is fabricated, and individual results vary; no outcome is guaranteed. Until consented stories are available, you can read verified Google reviews for our Coral Springs clinic in the next section.`,
      ],
    },
    {
      heading: "Google reviews for the Coral Springs clinic",
      paragraphs: [
        `You can read verified Google reviews for Neuro Wellness TMS Centers of America in Coral Springs, Florida on our Google Business Profile. Reviews reflect individual experiences and are not a guarantee of any outcome. [[Read our Google reviews|/contactus]] or [[see our Coral Springs location|/tms-coral-springs-fl]].`,
      ],
      links: [
        {
          to: "/contactus",
          text: "Contact us for directions and reviews",
        },
      ],
    },
    {
      heading: "What these results mean for you",
      paragraphs: [
        `The 78.6% remission rate is the strongest published evidence for SAINT®, but it is a group result from a specific trial of a specific system in adults with major depressive disorder whose depression had not improved enough on prior antidepressant medication. Your individual result depends on your diagnosis, history, and screening. SAINT® is FDA-cleared for adults 22 and older with major depressive disorder. [[See if you may be a candidate|/learn/who-qualifies-for-saint-tms]] or [[verify your insurance benefits|/check-insurance-options]]. SAINT® at Neuro Wellness starts at $25,000; coverage varies by plan and we verify your benefits for free.`,
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32463539/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: [
    {
      title: `SAINT® vs $9,900 "Accelerated TMS": Not the Same Treatment`,
      href: "/learn/saint-vs-accelerated-tms",
      description: "Why the trial results belong to SAINT® only",
    },
    {
      title: "What Is SAINT® TMS?",
      href: "/learn/what-is-saint-tms",
      description: "The FDA-cleared, fMRI-guided 5-day protocol explained",
    },
    {
      title: "Who Qualifies for SAINT® TMS?",
      href: "/learn/who-qualifies-for-saint-tms",
      description: "Eligibility, checklists, and next steps",
    },
    {
      title: "Is It Really SAINT®? How to Verify a Clinic",
      href: "/learn/what-is-a-verified-saint-provider",
      description: "A 3-step check and red flags before you pay",
    },
    {
      title: "SAINT® TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "What SAINT® costs and how to plan for payment",
    },
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol offered at our clinic",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "What the trials show", href: "#what-the-saint-trials-show" },
    { label: "Patient stories", href: "#patient-stories" },
    {
      label: "Google reviews",
      href: "#google-reviews-for-the-coral-springs-clinic",
    },
    {
      label: "What results mean for you",
      href: "#what-these-results-mean-for-you",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. Neuro Wellness TMS Centers of America is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "See if I may be a candidate",
    primaryHref: "/learn/who-qualifies-for-saint-tms",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
